import './index.less';

import { message } from 'antd';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserProfile } from '@/hooks/useUserProfile';
import { registerPermisson } from '@/services/user';
import {
  getFeedbackAllowed as getFeedbackAllowedApi,
  submitFeedBack as submitFeedBackApi,
} from '@/services/user-feedback';
import isApp from '@/utils/ua';

import FeedbackDialog from '../FeedbackDialog/FeedbackDialog';
import FeedbackErrorDialog from '../FeedbackErrorDialog/FeedbackErrorDialog';
import FeedbackUpload from '../FeedbackUpload/FeedbackUpload';

const FeedbackBox = ({ onLogin }: { onLogin: () => void }) => {
  const { t: $t, i18n } = useTranslation('user-feedback');
  const { userInfo, loading } = useUserProfile();
  const [scoreAllowedLoading, setScoreAllowedLoading] = useState(true);
  const [scoreAllowed, setScoreAllowed] = useState(1);
  const [currentScore, setCurrentScore] = useState<number | null>(null);
  const [textareaFocus, setTextareaFocus] = useState(false);
  const [freeComment, setFreeComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ipInfo, setIpInfo] = useState<{ code: string; name_en?: string }>({
    code: 'Unknow',
  });
  const [imgUrls, setImgUrls] = useState<string[]>([]);
  const dialogRef = useRef<any>(null);
  const errorDialogRef = useRef<any>(null);

  const getFeedbackAllowed = () => {
    setScoreAllowedLoading(true);
    getFeedbackAllowedApi()
      .then((ret) => {
        const { scoreAllowed } = ret;
        setScoreAllowed(+scoreAllowed);
        setScoreAllowedLoading(false);
      })
      .catch(() => {
        setScoreAllowedLoading(false);
      });
  };

  const getIpInfo = () => {
    registerPermisson().then((res) => {
      setIpInfo(res);
      return res;
    });
  };

  useEffect(() => {
    if (loading) {
      return;
    }
    if (userInfo.id) {
      getFeedbackAllowed();
    } else {
      setScoreAllowedLoading(false);
    }
  }, [userInfo, loading]);

  useEffect(() => {
    getIpInfo();
  }, []);

  const clickScore = (score: number) => {
    if (!userInfo.id) {
      onLogin();
    } else {
      setCurrentScore(score);
    }
  };

  const getPNSOrUserFeedBack = (freeComment: string, score: number | null) => {
    if ((freeComment && score) || score) return 'NPS';
    if (freeComment) return 'UserFeedback';
    return 'NPS';
  };

  const submitSuccess = () => {
    setCurrentScore(null);
    setFreeComment('');
    setImgUrls([]);
  };

  const handleSubmit = () => {
    // 若用户没有登录不能调用接口
    if (!userInfo.id) {
      onLogin();
      return;
    }

    if (!currentScore && !freeComment) {
      return;
    }

    if (freeComment && freeComment.length > 500) {
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    const params = {
      language: i18n.language,
      ip_country: ipInfo.name_en,
      user_country: userInfo.country_code || ipInfo.name_en,
      platform: isApp() ? 'App' : 'Web',
      function_module: getPNSOrUserFeedBack(freeComment, currentScore),
      free_comment: DOMPurify.sanitize(freeComment, { ALLOWED_TAGS: [] }),
      score: currentScore ? currentScore : -1,
      score_type: 'NPS',
      image_urls: imgUrls,
    };
    // 若用户既点击评分或者评论提交才调用接口
    if (freeComment || currentScore) {
      submitFeedBackApi(params)
        .then((res) => {
          const { scoreAllowed } = res as any;
          if (scoreAllowed.toString()) {
            submitSuccess();
            setScoreAllowed(+scoreAllowed);
          }
          setIsSubmitting(false);
          dialogRef.current?.open();
        })
        .catch((err) => {
          setIsSubmitting(false);
          if (err?.ret_code) {
            const { ret_code, ret_msg } = err;
            if (ret_code === 37001 || ret_code === 403003) {
              // 提交太频繁触发后端频率限制
              // 每个uid每分钟只能提交 5 次评论(37001)
              // 每个uid每3个月只能提交 1 次评分(403003)
              errorDialogRef.current?.open?.(
                $t('newFeedbacksubmissionsLimitTitle'),
                $t('newFeedbacksubmissionsLimitcontent'),
              );
            } else {
              const i18nMessage = $t(ret_code, { ns: 'inverse-error-code' });
              message.error(
                `${ret_code}: ${
                  i18nMessage === `${ret_code}` ? ret_msg : i18nMessage
                }`,
              );
            }
          } else {
            message.error('Something went wrong');
          }
        });
      // .finally(() => {});
    }
  };

  return (
    <div className="feedback-box">
      {scoreAllowed && !scoreAllowedLoading ? (
        <div className="recommend-score">
          <div className="recommend-score-title">
            {$t('feedbackQuestionRecommend')}
            <span style={{ color: '#FC3030' }}> *</span>
          </div>
          <div className="score-wrap">
            <div className="score-inner">
              <div className="recommend-score-likely-text-sm">
                0 - {$t('feedbackQuesitionNotLikelyAtAll')}
              </div>
              <div
                className={classNames('score-box', {
                  'score-box-disabled': loading,
                })}
              >
                {[...Array(11).keys()].map((item, index) => (
                  <div
                    className={classNames('score', {
                      'score-active': item == currentScore,
                    })}
                    key={index}
                    onClick={() => {
                      clickScore(item);
                    }}
                  >
                    {item}
                  </div>
                ))}
              </div>
              <div className="recommend-score-likely-text-sm textalign-right">
                10 - {$t('feedbackQuesitionExtremelyLikely')}
              </div>
              <div className="recommend-score-likely-text">
                <span>{$t('feedbackQuesitionNotLikelyAtAll')}</span>
                <span>{$t('feedbackQuesitionNeutral')}</span>
                <span>{$t('feedbackQuesitionExtremelyLikely')}</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="recommend-share">
        <div className="recommend-share-title">
          {scoreAllowed
            ? $t('feedbackQuesitionShareReason')
            : $t('feedbackShareReasonNoneScore')}
          {!scoreAllowed ? <span style={{ color: '#FC3030' }}> *</span> : null}
        </div>
        <div
          className={classNames(
            'input-wrap',
            { 'input-wrap-focus': textareaFocus },
            { 'input-wrap-error': freeComment.length > 500 },
            { 'input-wrap-disabled': loading },
          )}
        >
          <textarea
            placeholder={$t('feedbackQuesitionShareReasonPlaceholder')}
            disabled={loading}
            value={freeComment || ''}
            onFocus={() => {
              if (!userInfo.id) {
                onLogin();
              } else {
                setTextareaFocus(true);
              }
            }}
            onBlur={() => {
              setTextareaFocus(false);
            }}
            readOnly={!userInfo.id}
            onChange={(e) => {
              setFreeComment(e.target.value);
            }}
          />
          <div
            className={classNames('word-count', {
              'word-count-error': freeComment.length > 500,
            })}
          >
            {freeComment.length} / 500
          </div>
        </div>
      </div>
      <div className="recommend-upload">
        <FeedbackUpload
          onLogin={onLogin}
          urls={imgUrls}
          onUpload={(urls: string[]) => {
            setImgUrls(urls);
          }}
        />
      </div>
      <div className="submit-btn-wrap">
        <button
          className={classNames({
            invalid:
              isSubmitting ||
              (currentScore === null && !freeComment) ||
              freeComment.length > 500,
          })}
          onClick={() => {
            handleSubmit();
          }}
        >
          {$t('feedbackSubmitText')}
        </button>
      </div>
      <FeedbackDialog ref={dialogRef} onEnsureCb={submitSuccess} />
      <FeedbackErrorDialog
        ref={errorDialogRef}
        isUpload={false}
        isManySubmission={true}
      />
      {/*
    <feedbackErrorDialog  :isManySubmission=true
                          ref="errorDialog" :langParams="langParams" :i18n="i18n" title={} content={} /> */}
    </div>
  );
};

export default FeedbackBox;
